import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { ChannelIcon } from "../../channels/components/channel-icon";
import { sendTimeText } from "../program-timeline";
import {
  CHANNEL_LABELS,
  CountPill,
  Dot,
  FolderName,
  StatusChip,
  TitleLink,
  isChannel,
} from "./timeline-parts";

import type { TimelineGroup, TimelineRow } from "../program-timeline";
import type { FC, ReactNode } from "react";

/** The table's cells: 12px apart and 16px at the sides, and the header row as in the design. */
const tableSx = {
  minWidth: 760,
  "& .MuiTableCell-root": { px: 1.5, py: 1 },
  "& .MuiTableCell-root:first-of-type": { pl: 2 },
  "& .MuiTableCell-root:last-of-type": { pr: 2 },
  "& .MuiTableBody-root .MuiTableCell-root": { borderColor: "divider" },
} as const;

/** The columns, with their widths; the Communication takes the rest. */
const COLUMNS: { label: string; width?: number }[] = [
  { label: "Timing", width: 88 },
  { label: "Communication" },
  { label: "Platform", width: 140 },
  { label: "Segment", width: 180 },
  { label: "Status", width: 120 },
  { label: "Actions", width: 104 },
];

const headSx = {
  bgcolor: "background.default",
  "& .MuiTableCell-root": {
    height: 40,
    py: 0,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.5,
    color: "text.secondary",
    whiteSpace: "nowrap",
    borderBottom: 1,
    borderColor: "divider",
  },
} as const;

/**
 * The groups as a table: a row for each group, with its count, then a row for each of its
 * Communications, with its timing, title and folder, channel, segment, status and actions. The
 * title's link covers its row.
 */
export const TimelineTable: FC<{
  groups: TimelineGroup[];
  folderOf: (row: TimelineRow) => string | null;
  actionsOf: (row: TimelineRow) => ReactNode;
}> = ({ groups, folderOf, actionsOf }) => (
  <Box sx={{ overflowX: "auto" }}>
    <Table aria-label="Communications" sx={tableSx}>
      <TableHead sx={headSx}>
        <TableRow>
          {COLUMNS.map(({ label, width }) => (
            <TableCell
              key={label}
              sx={{ width, textAlign: label === "Actions" ? "right" : undefined }}
            >
              {label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      {groups.map((group) => (
        <TableBody key={group.key} aria-label={group.name}>
          <TableRow sx={{ bgcolor: "action.hover" }}>
            <TableCell colSpan={COLUMNS.length} sx={{ py: "10px !important" }}>
              <Stack
                direction="row"
                useFlexGap
                sx={{ alignItems: "center", flexWrap: "wrap", gap: 1.25 }}
              >
                <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                  <Dot color={group.color} />
                  <Typography
                    component="h3"
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      letterSpacing: 0.5,
                      textTransform: "uppercase",
                    }}
                  >
                    {group.name}
                  </Typography>
                </Stack>
                {group.code ? (
                  <Typography
                    variant="caption"
                    component="code"
                    sx={{ fontFamily: "monospace", color: "text.secondary" }}
                  >
                    {group.code}
                  </Typography>
                ) : null}
                <Box sx={{ flex: 1 }} />
                <CountPill count={group.count} />
              </Stack>
            </TableCell>
          </TableRow>
          {group.stops.flatMap((stop) =>
            stop.rows.map((row) => {
              const folder = folderOf(row);
              return (
                <TableRow key={row.id} hover sx={{ position: "relative", height: 56 }}>
                  <TableCell
                    title={`${stop.title}${row.send_at ? `, ${sendTimeText(row.send_at)}` : ""}`}
                    sx={{
                      fontFamily: "monospace",
                      fontSize: 13,
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {stop.short}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 0 }}>
                    <Typography component="div" sx={{ fontSize: 14, lineHeight: "20px" }}>
                      <TitleLink row={row} covers />
                    </Typography>
                    {folder ? (
                      <Typography component="div" sx={{ fontSize: 12, lineHeight: "18px" }}>
                        <FolderName name={folder} fontSize={12} />
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} sx={{ alignItems: "center", fontSize: 13 }}>
                      <ChannelIcon
                        channel={isChannel(row.channel) ? row.channel : null}
                        sx={{ fontSize: 20 }}
                      />
                      <span>{isChannel(row.channel) ? CHANNEL_LABELS[row.channel] : "—"}</span>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ fontSize: 13 }}>{row.segment_name ?? "—"}</TableCell>
                  <TableCell>
                    <StatusChip status={row.status} />
                  </TableCell>
                  <TableCell sx={{ py: "0 !important" }}>
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>{actionsOf(row)}</Box>
                  </TableCell>
                </TableRow>
              );
            }),
          )}
        </TableBody>
      ))}
    </Table>
  </Box>
);
